<script setup>
import {computed, ref, watch} from 'vue'
import {formatDate, translateRole} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import Textarea from '@/components/forms/Textarea.vue';
import Select from '@/components/forms/Select.vue';
import {authData} from '@/services/AuthService';
import {ErrorMessage, Field, Form} from 'vee-validate';
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const props = defineProps({
  user: {type: Object, required: false},
});

const user = ref({name: '', email: '', phone: '', role: 'ADMIN', password: '', password_conf: ''});
const isSuperAdmin = computed(() => authData?.loggedUser?.role === 'SUPERADMIN');
const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);
const closeModalWith = (value) => {
  if (value === 'confirm' && passwordMismatch.value) {
    return;
  }

  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', user.value);
    } else {
      emit('cancel');
    }
  }, 300);
}

if (props.user) {
  user.value = props.user;
}

function openModal() {
  isOpen.value = true
}

const passwordMismatch = ref(false);

watch([() => user.value.password, () => user.value.password_conf], () => {
  passwordMismatch.value = user.value.password && user.value.password_conf && user.value.password !== user.value.password_conf;
});
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
          <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
            <div class="modal-header">
              <DialogTitle v-if="user.id" as="h3" class="modal-title">
                {{ t('createUpdateUserModal.client_edit') }}
              </DialogTitle>
              <DialogTitle v-else as="h3" class="modal-title">
                {{ t('createUpdateUserModal.client_create') }}
              </DialogTitle>
              <i class="btn-close cursor-pointer" @click="closeModalWith('cancel')" aria-label="Close"></i>
            </div>
            <Form ref="userForm" v-on:submit="closeModalWith('confirm')">
              <div class="modal-body">
                <div class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="nameInput" class="form-label">{{ t('createUpdateUserModal.name') }}</label>
                    <Field v-model="user.name" id="nameInput" name="nameInput" class="form-control" rules="required"/>
                    <ErrorMessage name="nameInput"/>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="emailInput" class="form-label">{{ t('createUpdateUserModal.email') }}</label>
                    <Field v-model="user.email" id="emailInput" name="emailInput" class="form-control" type="email" rules="required|email"/>
                    <ErrorMessage name="emailInput"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="phoneInput" class="form-label">{{ t('createUpdateUserModal.phone_number') }}</label>
                    <Field v-model="user.phone" name="phoneInput" id="phoneInput" class="form-control" type="text" rules="required|phoneNumber"/>
                    <ErrorMessage name="phoneInput"/>
                  </div>
                </div>
                <div v-if="isSuperAdmin" class="row mb-3">
                  <div class="col-12 form-group">
                    <label for="roleInput" class="form-label">{{ t('createUpdateUserModal.role') }}</label>
                    <select v-model="user.role" id="roleInput" class="form-select">
                      <option value="SUPERADMIN">SUPERADMIN</option>
                      <option value="ADMIN">ADMIN</option>
                      <option value="BACK_OFFICE">BACK_OFFICE</option>
                      <option value="OFFICE">OFFICE</option>
                      <!-- Tyto role lze nastavit jen přes poradce u konkrétního Zprostředkovatele (IntermediaryPersonService) -
                           tam se zaručeně udržuje vazba na Intermediary/strom. V tomto genericém selectu proto zůstávají
                           jen viditelné (aby šla vidět aktuální role uživatele), ale nejdou nově vybrat; BE to navíc
                           vynucuje v UserService::save(). -->
                      <option value="INTERMEDIARY" disabled>{{ translateRole('INTERMEDIARY') }}</option>
                      <option value="INTERMEDIARY_ADMIN" disabled>{{ translateRole('INTERMEDIARY_ADMIN') }}</option>
                      <option value="INTERMEDIARY_MANAGER" disabled>{{ translateRole('INTERMEDIARY_MANAGER') }}</option>
                      <option value="INTERMEDIARY_LEADER" disabled>{{ translateRole('INTERMEDIARY_LEADER') }}</option>
                      <option value="CLIENT">{{ translateRole('CLIENT') }}</option>
                    </select>
                  </div>
                </div>
                <div v-if="!user.id" class="row">
                  <div class="col-12 col-md-6 form-group">
                    <label for="passwordInput" class="form-label">{{ t('createUpdateUserModal.password') }}</label>
                    <Field v-model="user.password" id="passwordInput" name="passwordInput" class="form-control" type="password" :rules="user.id ? '' : 'required'"/>
                    <ErrorMessage name="passwordInput"/>
                  </div>
                  <div class="col-12 col-md-6 form-group">
                    <label for="passwordConfirmationInput" class="form-label">{{ t('createUpdateUserModal.password_check') }}</label>
                    <Field v-model="user.password_conf" id="passwordConfirmationInput" name="passwordConfirmationInput" class="form-control" type="password" :rules="user.id ? '' : 'required'"/>
                    <ErrorMessage name="passwordConfirmationInput"/>
                    <span v-if="passwordMismatch" class="text-danger">{{ t('createUpdateUserModal.password_mismatch') }}</span>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createUpdateUserModal.cancel') }}</button>
                <button class="btn btn-primary btn-md">{{ t('createUpdateUserModal.save') }}</button>
              </div>
            </Form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

